import { ReactNode } from 'react';
import { useRouter } from 'next/router';
import { useReduxState } from 'hooks';
import { Link } from 'components/common';
import { Icon } from 'components/common/icons';

import type { IconProp } from 'components/common/icons/Icon';

interface Props {
  backLinkAs?: string;
  backLinkHref?: string;
  children?: ReactNode;
  icon?: IconProp;
  iconOnClick?: () => void;
  simpleBack?: boolean;
  title?: string;
  titleContent?: ReactNode;
  titleDescription?: string;
  titleTooltip?: string;
}

const PageHeader = ({
  backLinkAs,
  backLinkHref,
  children,
  icon,
  iconOnClick,
  simpleBack = false,
  title,
  titleContent,
  titleDescription,
  titleTooltip
}: Props) => {
  const { messages, theme } = useReduxState();

  const router = useRouter();

  return (
    <div className="page-header">
      <div className="container">
        <div className="row-md">
          <div className="col col-center">
            <div className="title-wrapper">
              {simpleBack ? (
                <button
                  aria-label={messages.commonGoBack}
                  className="back-link"
                  onClick={() => router.back()}
                >
                  <Icon icon="arrow-left" />
                </button>
              ) : backLinkHref ? (
                <Link as={backLinkAs} href={backLinkHref}>
                  <div aria-label={messages.commonGoBack} className="back-link">
                    <Icon icon="arrow-left" />
                  </div>
                </Link>
              ) : icon && iconOnClick ? (
                <button className="icon icon-clickable" onClick={iconOnClick}>
                  <Icon icon={icon} />
                </button>
              ) : icon ? (
                <div className="icon">
                  <Icon icon={icon} />
                </div>
              ) : null}
              <div className="title">
                {titleContent || (
                  <>
                    <h2
                      className="title-heading"
                      {...(titleTooltip
                        ? { 'data-tooltip': titleTooltip }
                        : {})}
                    >
                      {title}
                      {titleTooltip ? (
                        <Icon
                          icon="question-circle"
                          style={{
                            fontSize: '1.3rem',
                            marginLeft: 7,
                            position: 'relative',
                            top: -1
                          }}
                        />
                      ) : null}
                    </h2>

                    {titleDescription ? (
                      <div className="title-description">
                        {titleDescription}
                      </div>
                    ) : null}
                  </>
                )}
              </div>
            </div>
          </div>

          {children ? <div className="col-min">{children}</div> : null}
        </div>
      </div>

      <style jsx>{`
        .page-header {
          border-bottom: 1px solid ${theme.lightGreyDark};
          padding: 18px 0;
        }
        .title-wrapper {
          align-items: normal;
          display: inline-flex;
        }
        .back-link,
        .icon {
          align-items: center;
          background-color: transparent;
          border: none;
          cursor: default;
          display: inline-flex;
          font-family: inherit;
          font-size: 1.2rem;
          justify-content: center;
          margin: 2px 8px 2px 0;
          padding: 0;
          text-decoration: none;
          transition: background-color 0.3s;
          height: 30px;
          width: 30px;
        }
        .icon {
          background-color: ${theme.pageIconBackgroundColor || 'white'};
          border-radius: 30px;
          color: ${theme.pageIconTextColor || theme.textColor};
        }
        .back-link,
        .icon-clickable {
          background-color: ${theme.pageIconBackgroundColor || 'white'};
          border-radius: 30px;
          color: ${theme.pageIconTextColorLighter || theme.textColorLighter};
          cursor: pointer;
        }
        .back-link:focus,
        .back-link:hover,
        .icon-clickable:focus,
        .icon-clickable:hover {
          color: ${theme.pageIconTextColor || theme.textColor};
        }
        .title-heading {
          color: ${theme.pageTextColor || theme.textColor};
          font-size: 1.8rem;
          line-height: 1.9;
          margin: 0;
        }
        .title-description {
          font-size: 1.3rem;
          font-weight: 400;
          color: ${theme.pageTextColorLighter || theme.textColorLighter};
          line-height: 1.2;
          margin-top: -5px;
          padding-left: 1px;
        }
      `}</style>
    </div>
  );
};

export default PageHeader;
